import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as AddIcon } from '@/assets/icons/ri-add-line.svg';
import { ReactComponent as EditIcon } from '@/assets/icons/ri-edit-line.svg';
import Button from '@/components/common/Button';
import BreadcrumbLayout from '@/components/common/CustomLayout/BreadcrumbLayout';
import Modal from '@/components/common/Modal';
import Table from '@/components/common/Table';
import { useAppDispatch, RootState } from '@/store';
import { getRoleList, createRole, editRole } from '@/store/role/action';
import { getUserVenues } from '@/store/user-info/action';
import useCustomPaging from '@/hooks/useCustomPaging';
import useSearchFilter from '@/hooks/useSearchFilter';
import { dynamicAttr } from '@/utils/fallback';
import { constructTableMetaFromPaging2 } from '@/utils/paging';
import {
	renderRoleFormFooter,
	renderRoleFormBody,
	renderRoleFormHeader,
} from '@/pages/Roles/Modal';

const renderRolePageHeader = (
	searchString: string,
	handleChangeSearchString: any,
	handleSubmitSearch: any,
	showModal: Function
) => (
	<BreadcrumbLayout>
		<div className="flex justify-between items-center gap-8 p-0">
			<p className="font-bold text-2xl text-black-primary">Role Setting</p>
			<div className="flex items-center gap-4">
				<input
					value={searchString}
					onChange={handleChangeSearchString}
					onKeyDown={handleSubmitSearch}
					name="searchString"
					type="text"
					className="flex items-center py-[8px] px-4 gap-2 border rounded border-solid border-stroke-subtle text-jsm font-normal"
					placeholder="Search for role"
				/>
				<div className="bg-grey-line-subtle h-6 w-1" />
				<Button
					type="submit"
					variant="primary"
					className="text-white"
					onClick={() => showModal()}
				>
					<AddIcon />
					<p>Add Role</p>
				</Button>
			</div>
		</div>
	</BreadcrumbLayout>
);

const renderAccessRightSection = (accessList: { [key: string]: any }[]) => {
	const venueList = new Map();
	const allList =[];
	const roleCodes = new Map();

	const featureCodeDetails = accessList.map((detail) => {
		roleCodes.set(detail.id, detail.feature_code)
		if (detail.details.length === 0) {
			allList.push(detail)
		}
		return detail.details
	});
	featureCodeDetails.forEach((venues) => {
		venues.forEach((venue) => {
			const venueAccess = venueList.get(venue?.venue?.name) || [];
			venueAccess.push(venue.role_access_id);
			venueList.set(venue?.venue?.name, venueAccess);
		});
	});
	return (
		<div className="flex flex-col gap-4 p-0">
			{ allList.length > 0 &&
				<div className="flex flex-col p-0 gap-2">
					<p className="font-bold text-sm text-grey">General</p>
					<div className="flex p-0 gap-2">
						{allList.map((access) =>
							(<div className="flex flex-col py-2 px-6 border border-grey-line-subtle rounded">
									{access.feature_code}
							</div>
							)
						)}
					</div>
				</div>
			}
			{Array.from(venueList.entries()).map((entry) => {
				const [key, value] = entry;
				return (
					<div className="flex flex-col p-0 gap-2">
						<p className="font-bold text-sm text-blue-secondary">{key}</p>
						<div className="flex p-0 gap-2">
							{value.map((accessId) =>
								(<div className="flex flex-col py-2 px-6 border border-grey-line-subtle rounded">
									{roleCodes.get(accessId)}
								</div>)
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};

const HEADERS: TableHeader[] = [
	{
		label: 'Role Name',
		key: 'name',
	},
	{
		label: 'Access Rights',
		key: 'access',
	},
	{
		label: 'Action',
		key: 'action',
	},
];
const constructTableData = (data, showModal) => {
	const tableBody = data.map((role) => {
		const { id, name, access_list } = role;
		return {
			id,
			value: {
				name,
				access: access_list,
				action: id,
			},
		};
	});
	const component = {
		name: (v) => (
			<div className="flex items-center h-full font-bold text-sm">{v}</div>
		),
		access: (v) => renderAccessRightSection(v),
		action: (v: string) => (
			<div>
				<Button
					className="border border-grey-line-subtle"
					type="button"
					variant="basic"
					onClick={() => {
						showModal(v);
					}}
				>
					<EditIcon />
					Edit
				</Button>
			</div>
		),
	};
	return {
		data: tableBody,
		component,
	};
};
const SORT: TableSort = {
	type: 'SERVER',
	handler: () => {},
};

const Roles = () => {
	const roleMethods = useForm();
	const roleState = useSelector((state: RootState) => state.roles.state.items);
	const venueState = useSelector(
		(state: RootState) => state.userInfo.state.venues
	);
	const paging = useSelector((state: RootState) => state.roles.state.paging);
	const [show, setShow] = useState(false);
	const [selectedRoleId, setSelectedRoleId] = useState('');
	const [venues, setVenues] = useState([]);
	const dispatch = useAppDispatch();
	const [searchParams,] = useSearchParams();
	const [currentPageFilter,,handlePageNumberChange] = useCustomPaging()
	const [
		searchString,
		currentSearchFilter,
		handleChangeSearchString,
		handleSubmitSearch
	] = useSearchFilter()
	const roleMeta: RoleFilter = {
		search: searchParams.get('search'),
	};

	const showModal = async (v?: string) => {
		await dispatch(getUserVenues({}));
		await setVenues([...venueState]);
		if (v) {
			setSelectedRoleId(v);
		}
		setShow(true);
	};
	const closeModal = () => {
		setSelectedRoleId('');
		setShow(false);
	};
	const submitModal = async () => {
		const roleData = roleMethods.getValues();
		const access_list = [];
		Object.keys(roleData)
			.filter(
				(access) =>
					!access.includes('_ACCESS') &&
					access !== 'name' &&
					access !== 'access_list' &&
					access !== 'VENUE_MASTER' &&
					roleData[`${access}_ACCESS`]?.length
			)
			.forEach((access) => {
				const payload = {
					feature_code: access,
					details: roleData[`${access}_ACCESS`].map((detail) => ({
						venue_id: detail.value,
					})),
				};
				access_list.push(payload);
			});
		if (roleData.CUSTOMER) {
			access_list.push({
				feature_code: 'CUSTOMER',
			});
		}
		if (roleData.VENUE_MASTER) {
			access_list.push({
				feature_code: 'VENUE_MASTER',
			});
		}
		const data = {
			name: roleData.name,
			access_list,
		};
		if (!selectedRoleId) {
			await dispatch(createRole(data));
		} else {
			await dispatch(editRole({ data, id: selectedRoleId }));
		}
		await dispatch(getRoleList(roleMeta));
		closeModal();
	};

	const setSortingParam = (key, order) => order === 'ASC' ? key : `-${key}`

	const getAllRole = () => {
		const params = {
			size: 10,
			...dynamicAttr('page', currentPageFilter),
			...dynamicAttr('search', currentSearchFilter),
			...dynamicAttr('sort', setSortingParam(searchParams.get('sortBy'), searchParams.get('order'))),
		}
		dispatch(getRoleList(params))
	}

	const roleData = (id: string) =>
		constructTableData(roleState, () => {}).data.find(
			(row) => row.value.action === id
		).value;

	useEffect(() => {
		(async () => {
			await getAllRole()
		})();
	}, [
		roleMeta.search,
		currentSearchFilter,
		currentPageFilter,
		searchParams.get('sortBy'),
		searchParams.get('order')
	]);
	useEffect(() => {
		if (selectedRoleId) {
			const role = roleData(selectedRoleId);
			const accesses = role.access.map((access) => ({
				[access.feature_code]: access.details.map((venue) => ({
					id: venue.venue_id,
					label: venue?.venue?.name,
				})),
			}));
			const initialRole = {
				name: role.name,
				access_list: role.access,
			};
			accesses.forEach((access) => {
				const key = Object.keys(access)[0];
				const value = access[key];
				initialRole[key] = value;
				initialRole[`${key}_ACCESS`] = value.map((val) => ({
					value: val.id,
					label: val.label,
				}));
			});
			roleMethods.reset({ ...initialRole });
		} else {
			roleMethods.reset({});
		}
	}, [roleMethods.reset, selectedRoleId]);

	return (
		<div className="flex flex-col w-full">
			{renderRolePageHeader(
				searchString,
				handleChangeSearchString,
				handleSubmitSearch,
				showModal
			)}
			<div className="flex flex-col p-4 w-full">
				<Table
					headers={HEADERS}
					sort={SORT}
					body={constructTableData(roleState, showModal)}
					meta={constructTableMetaFromPaging2(paging)}
					pageNumberHandler={handlePageNumberChange}
				/>
			</div>
			{show && (
				<Modal
					show={show}
					header={renderRoleFormHeader}
					body={renderRoleFormBody(
						{ roleMethods },
						selectedRoleId,
						venues,
						() => submitModal
					)}
					footer={renderRoleFormFooter(
						() => closeModal,
						() => submitModal
					)}
					onClick={() => closeModal}
					rootId="portal-role-form"
				/>
			)}
		</div>
	);
};

export default Roles;
